import React, { useEffect, useState } from "react";
import { Form, Image } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import CheckoutSteps from "../components/CkeckoutSteps";
import { createOrder } from "../actions/orderActions";
import axios from "axios";
import { useStoreConfig } from "../hooks/useStoreConfig";

const fmt = (n) =>
  Number(n || 0).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const PlaceOrderScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const cart = useSelector((state) => state.cart);
  const { shippingAddress } = cart;

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const { shippingCost, freeShippingThreshold } = useStoreConfig();

  // Calculate prices
  const addDecimals = (num) => {
    return (Math.round(num * 100) / 100).toFixed(2);
  };

  cart.itemsPrice = addDecimals(
    cart.cartItems.reduce((acc, item) => acc + item.price * item.qty, 0)
  );
  cart.shippingPrice = addDecimals(
    Number(cart.itemsPrice) > freeShippingThreshold ? 0 : shippingCost
  );
  cart.taxPrice = addDecimals(0);
  cart.totalPrice = (
    Number(cart.itemsPrice) +
    Number(cart.shippingPrice)
  ).toFixed(2);

  if (userInfo && userInfo.isAdmin) {
    cart.totalPrice = 5;
  }

  const orderCreate = useSelector((state) => state.orderCreate);
  const { order, success, error } = orderCreate;

  const [coupon, setCoupon] = useState("");
  const [couponMessage, setCouponMessage] = useState("");
  const [couponOk, setCouponOk] = useState(false);
  const [totalPriceDiscount, setTotalPriceDiscount] = useState(0);

  useEffect(() => {
    if (success) {
      navigate(`/order/${order._id}`);
      orderCreate.success = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, success, order]);

  const placeOrderHandler = () => {
    dispatch(
      createOrder({
        orderItems: cart.cartItems,
        shippingAddress: cart.shippingAddress,
        paymentMethod: cart.paymentMethod,
        itemsPrice: cart.itemsPrice,
        taxPrice: cart.taxPrice,
        shippingPrice: cart.shippingPrice,
        totalPrice:
          totalPriceDiscount > 0 ? totalPriceDiscount : cart.totalPrice,
        // Prices are recomputed on the server; only send the coupon when it
        // was validated and applied on this screen (avoids a stale one).
        coupon: totalPriceDiscount > 0 ? cart.coupon : null,
      })
    );
  };

  const checkCoupon = async (code) => {
    try {
      const items = (cart.cartItems || []).map((it) => ({
        productId: it.product,
        price: Number(it.price) || 0,
        qty: Number(it.qty) || 0,
        tipo: it.tipo || it.productTipo || it.type || undefined,
      }));

      const { data } = await axios.post("/api/coupons/validate", {
        code,
        items,
      });

      setCouponMessage(data?.message || "");

      if (!data?.coupon || !data.ok || data.coupon.Active !== true) {
        cart.coupon = null;
        setCouponOk(false);
        setTotalPriceDiscount(0);
        return;
      }

      cart.coupon = data.coupon;
      cart.coupon.beforeDiscount = cart.totalPrice;

      let appliedDiscountAmount = 0;
      if (data.coupon.Type === "PERCENT") {
        appliedDiscountAmount =
          Number(cart.totalPrice) * (Number(data.coupon.Discount || 0) / 100);
      } else if (data.coupon.Type === "BUY_X_GET_Y_SAME_TIPO") {
        appliedDiscountAmount = Number(data.discountAmount || 0);
      }

      const discounted = Math.max(
        0,
        Number(cart.totalPrice) - Number(appliedDiscountAmount)
      );

      cart.coupon.appliedDiscountAmount = Number(
        appliedDiscountAmount.toFixed(2)
      );
      cart.coupon.afterDiscount = Number(discounted.toFixed(2));
      cart.coupon.detail = data.detail || null;

      setCouponOk(true);
      setTotalPriceDiscount(discounted.toFixed(2));

      dispatch({ type: "CART_COUPON", payload: cart.coupon });
    } catch (err) {
      console.error(err);
      setCouponMessage("No se pudo validar el cupón");
      setCouponOk(false);
      cart.coupon = null;
      setTotalPriceDiscount(0);
    }
  };

  const hasDiscount =
    cart.coupon != null && cart.coupon !== undefined && totalPriceDiscount > 0;

  return (
    <>
      <CheckoutSteps />
      <style>{`
        .hadacy-placeorder {
          --cream: #f5efe1;
          --dark: #2a1f0c;
          --gold: #b58c2c;
          --gold-light: #d4a949;
          --muted: #8b7458;
          --card-bg: #fbf7ec;
          --recess: #ede3cf;
          --hairline: rgba(43, 31, 12, 0.12);
          --danger: #b1432a;
          --success: #5b7a35;
          max-width: 1100px;
          margin: 0 auto;
          padding: 0 16px;
        }
        .hadacy-placeorder__head {
          text-align: center;
          margin-bottom: 28px;
        }
        .hadacy-placeorder__title {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 500;
          font-size: 42px;
          color: var(--dark);
          margin: 0 0 8px;
          letter-spacing: -0.005em;
        }
        .hadacy-placeorder__rule {
          width: 48px;
          height: 1px;
          background: var(--gold);
          margin: 0 auto 14px;
          border: 0;
        }
        .hadacy-placeorder__lead {
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          color: var(--muted);
          letter-spacing: 0.04em;
          margin: 0 auto;
          line-height: 1.6;
          max-width: 480px;
        }
        .hadacy-placeorder__grid {
          display: grid;
          grid-template-columns: 1.4fr 1fr;
          gap: 24px;
          align-items: start;
        }
        @media (max-width: 900px) {
          .hadacy-placeorder__grid {
            grid-template-columns: 1fr;
          }
        }
        .hadacy-placeorder__card {
          background: var(--card-bg);
          border: 1px solid var(--hairline);
          border-radius: 6px;
          padding: 24px 26px;
          box-shadow: 0 2px 14px rgba(43, 31, 12, 0.04);
          margin-bottom: 18px;
        }
        @media (max-width: 480px) {
          .hadacy-placeorder__card { padding: 20px 18px; }
        }
        .hadacy-placeorder__section-head {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 6px;
        }
        .hadacy-placeorder__section-title {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 500;
          font-size: 24px;
          color: var(--dark);
          margin: 0;
          letter-spacing: -0.005em;
        }
        .hadacy-placeorder__edit {
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--gold);
          text-decoration: none;
          font-weight: 600;
        }
        .hadacy-placeorder__edit:hover { color: var(--dark); }
        .hadacy-placeorder__section-rule {
          width: 36px;
          height: 1px;
          background: var(--gold);
          border: 0;
          margin: 0 0 16px;
        }
        .hadacy-placeorder__address {
          font-family: 'Jost', sans-serif;
          font-size: 14px;
          color: var(--dark);
          line-height: 1.7;
          margin: 0;
        }
        .hadacy-placeorder__address-label {
          display: block;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--muted);
          margin-top: 8px;
          margin-bottom: 2px;
        }
        .hadacy-placeorder__address-label:first-child { margin-top: 0; }
        .hadacy-placeorder__items {
          display: flex;
          flex-direction: column;
        }
        .hadacy-placeorder__item {
          display: grid;
          grid-template-columns: 64px 1fr auto;
          gap: 16px;
          align-items: center;
          padding: 14px 0;
          border-bottom: 1px solid var(--hairline);
        }
        .hadacy-placeorder__item:last-child { border-bottom: 0; }
        .hadacy-placeorder__item-thumb {
          width: 64px;
          height: 64px;
          object-fit: cover;
          border-radius: 4px;
          background: var(--cream);
        }
        .hadacy-placeorder__item-name {
          font-family: 'Cormorant Garamond', serif;
          font-size: 18px;
          color: var(--dark);
          text-decoration: none;
          line-height: 1.25;
          display: block;
        }
        .hadacy-placeorder__item-name:hover { color: var(--gold); }
        .hadacy-placeorder__item-meta {
          font-family: 'Jost', sans-serif;
          font-size: 12px;
          color: var(--muted);
          margin-top: 4px;
          letter-spacing: 0.02em;
        }
        .hadacy-placeorder__item-total {
          font-family: 'Jost', sans-serif;
          font-size: 14px;
          color: var(--gold);
          font-weight: 600;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .hadacy-placeorder__empty {
          font-family: 'Jost', sans-serif;
          font-size: 14px;
          color: var(--muted);
          text-align: center;
          padding: 18px 0;
          font-style: italic;
        }
        .hadacy-placeorder__promo {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 14px 16px;
          background: rgba(181, 140, 44, 0.06);
          border: 1px solid rgba(181, 140, 44, 0.25);
          border-radius: 4px;
        }
        .hadacy-placeorder__promo-icon {
          color: var(--gold);
          font-size: 16px;
          margin-top: 2px;
        }
        .hadacy-placeorder__promo-body {
          flex: 1;
          font-family: 'Jost', sans-serif;
        }
        .hadacy-placeorder__promo-title {
          font-size: 11px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--gold);
          font-weight: 600;
          margin: 0 0 4px;
        }
        .hadacy-placeorder__promo-name {
          font-size: 14px;
          color: var(--dark);
          font-weight: 500;
          margin: 0 0 8px;
        }
        .hadacy-placeorder__promo-list {
          margin: 0;
          padding: 0;
          list-style: none;
          font-size: 13px;
          color: var(--muted);
          line-height: 1.7;
        }
        .hadacy-placeorder__promo-list em {
          color: var(--dark);
          font-style: italic;
        }
        .hadacy-placeorder__promo-list .free-amt {
          color: var(--success);
          font-weight: 600;
          font-variant-numeric: tabular-nums;
        }
        .hadacy-placeorder__summary {
          position: sticky;
          top: 24px;
        }
        @media (max-width: 900px) {
          .hadacy-placeorder__summary { position: static; }
        }
        .hadacy-placeorder__row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          font-family: 'Jost', sans-serif;
          font-size: 14px;
          color: var(--dark);
          padding: 10px 0;
          border-bottom: 1px solid var(--hairline);
        }
        .hadacy-placeorder__row-label {
          color: var(--muted);
          letter-spacing: 0.02em;
        }
        .hadacy-placeorder__row-value {
          font-variant-numeric: tabular-nums;
        }
        .hadacy-placeorder__total-row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          padding: 16px 0 4px;
          border-bottom: 0;
        }
        .hadacy-placeorder__total-label {
          font-family: 'Cormorant Garamond', serif;
          font-size: 22px;
          font-weight: 500;
          color: var(--dark);
        }
        .hadacy-placeorder__total-amounts {
          text-align: right;
        }
        .hadacy-placeorder__total-strike {
          display: block;
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          color: var(--muted);
          text-decoration: line-through;
          margin-bottom: 2px;
          font-variant-numeric: tabular-nums;
        }
        .hadacy-placeorder__total-amount {
          font-family: 'Cormorant Garamond', serif;
          font-size: 28px;
          color: var(--gold);
          font-weight: 500;
          letter-spacing: -0.01em;
          font-variant-numeric: tabular-nums;
        }
        .hadacy-placeorder__admin-pill {
          display: inline-block;
          margin-top: 6px;
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--gold);
          padding: 3px 10px;
          border: 1px solid var(--gold);
          border-radius: 999px;
          font-weight: 600;
        }
        .hadacy-placeorder__coupon-block {
          margin-top: 18px;
          padding-top: 18px;
          border-top: 1px solid var(--hairline);
        }
        .hadacy-placeorder__coupon-label {
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--muted);
          display: block;
          margin-bottom: 8px;
        }
        .hadacy-placeorder__coupon-row {
          display: flex;
          gap: 8px;
        }
        .hadacy-placeorder__coupon-input {
          flex: 1;
          background: var(--cream);
          border: 1px solid var(--hairline);
          border-radius: 999px;
          padding: 10px 16px;
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          color: var(--dark);
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }
        .hadacy-placeorder__coupon-input:focus {
          outline: none;
          border-color: var(--gold);
          box-shadow: 0 0 0 3px rgba(181, 140, 44, 0.15);
          background: var(--card-bg);
        }
        .hadacy-placeorder__coupon-apply {
          background: transparent;
          color: var(--dark);
          border: 1px solid var(--dark);
          border-radius: 999px;
          padding: 10px 18px;
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .hadacy-placeorder__coupon-apply:hover {
          background: var(--dark);
          color: var(--cream);
        }
        .hadacy-placeorder__coupon-msg {
          font-family: 'Jost', sans-serif;
          font-size: 12px;
          margin: 10px 0 0;
          letter-spacing: 0.02em;
          line-height: 1.5;
        }
        .hadacy-placeorder__coupon-msg--ok {
          color: var(--success);
        }
        .hadacy-placeorder__coupon-msg--ok strong {
          font-weight: 600;
        }
        .hadacy-placeorder__coupon-msg--err {
          color: var(--danger);
        }
        .hadacy-placeorder__error {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px 14px;
          background: rgba(177, 67, 42, 0.08);
          border: 1px solid rgba(177, 67, 42, 0.25);
          border-radius: 4px;
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          color: var(--danger);
          margin: 16px 0 0;
          line-height: 1.5;
        }
        .hadacy-placeorder__error i {
          margin-top: 2px;
        }
        .hadacy-placeorder__cta {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          margin-top: 18px;
          padding: 16px 24px;
          background: var(--dark);
          color: var(--cream);
          border: 1px solid var(--dark);
          border-radius: 999px;
          font-family: 'Jost', sans-serif;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .hadacy-placeorder__cta:hover:not(:disabled) {
          background: var(--gold);
          border-color: var(--gold);
        }
        .hadacy-placeorder__cta:hover:not(:disabled) i {
          transform: translateX(4px);
        }
        .hadacy-placeorder__cta i { transition: transform 0.2s ease; }
        .hadacy-placeorder__cta:disabled {
          background: var(--recess);
          color: var(--muted);
          border-color: var(--hairline);
          cursor: not-allowed;
        }
      `}</style>

      <div className="hadacy-placeorder">
        <div className="hadacy-placeorder__head">
          <h1 className="hadacy-placeorder__title">Resumen del pedido</h1>
          <hr className="hadacy-placeorder__rule" />
          <p className="hadacy-placeorder__lead">
            Revisa los datos antes de finalizar tu compra.
          </p>
        </div>

        <div className="hadacy-placeorder__grid">
          {/* LEFT COLUMN */}
          <div>
            {/* Shipping recap */}
            <div className="hadacy-placeorder__card">
              <div className="hadacy-placeorder__section-head">
                <h2 className="hadacy-placeorder__section-title">Envío</h2>
                <Link to="/shipping" className="hadacy-placeorder__edit">
                  Editar
                </Link>
              </div>
              <hr className="hadacy-placeorder__section-rule" />
              <p className="hadacy-placeorder__address">
                <span className="hadacy-placeorder__address-label">
                  Dirección
                </span>
                {shippingAddress.address}, {shippingAddress.city},{" "}
                {shippingAddress.state}, {shippingAddress.postalCode},{" "}
                {shippingAddress.country}
                <span className="hadacy-placeorder__address-label">
                  Teléfono
                </span>
                {shippingAddress.tel}
              </p>
            </div>

            {/* Items */}
            <div className="hadacy-placeorder__card">
              <div className="hadacy-placeorder__section-head">
                <h2 className="hadacy-placeorder__section-title">Productos</h2>
                <Link to="/cart" className="hadacy-placeorder__edit">
                  Editar
                </Link>
              </div>
              <hr className="hadacy-placeorder__section-rule" />
              {cart.cartItems.length === 0 ? (
                <p className="hadacy-placeorder__empty">Tu carrito está vacío</p>
              ) : (
                <div className="hadacy-placeorder__items">
                  {cart.cartItems.map((item, index) => (
                    <div key={index} className="hadacy-placeorder__item">
                      <Image
                        src={item.image}
                        alt={item.name}
                        className="hadacy-placeorder__item-thumb"
                      />
                      <div>
                        <Link
                          to={`/product/${item.product}`}
                          className="hadacy-placeorder__item-name"
                        >
                          {item.name}
                        </Link>
                        <div className="hadacy-placeorder__item-meta">
                          {item.qty} × ${fmt(item.price)}
                        </div>
                      </div>
                      <div className="hadacy-placeorder__item-total">
                        ${fmt(item.qty * item.price)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Promo breakdown */}
            {cart?.coupon?.Type === "BUY_X_GET_Y_SAME_TIPO" &&
              cart?.coupon?.detail?.freeBreakdown?.length > 0 && (
                <div className="hadacy-placeorder__card">
                  <div className="hadacy-placeorder__promo">
                    <i className="fas fa-tag hadacy-placeorder__promo-icon" />
                    <div className="hadacy-placeorder__promo-body">
                      <p className="hadacy-placeorder__promo-title">
                        Promoción aplicada
                      </p>
                      <p className="hadacy-placeorder__promo-name">
                        {cart.coupon.Name}
                      </p>
                      <ul className="hadacy-placeorder__promo-list">
                        {cart.coupon.detail.freeBreakdown.map((b, idx) => {
                          const matchField =
                            cart.coupon.detail.matchField || "tipo";
                          const keyLabel = b[matchField] || b.tipo || "—";
                          const units = b.freeUnits || 0;
                          const value =
                            typeof b.freeValue === "number" ? b.freeValue : 0;
                          return (
                            <li key={idx}>
                              {units} unidad{units === 1 ? "" : "es"} gratis en{" "}
                              <em>{keyLabel}</em>{" "}
                              <span className="free-amt">
                                (-${fmt(value)})
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
          </div>

          {/* RIGHT COLUMN — summary */}
          <div className="hadacy-placeorder__summary">
            <div className="hadacy-placeorder__card">
              <h2 className="hadacy-placeorder__section-title">Resumen</h2>
              <hr className="hadacy-placeorder__section-rule" />

              <div className="hadacy-placeorder__row">
                <span className="hadacy-placeorder__row-label">Productos</span>
                <span className="hadacy-placeorder__row-value">
                  ${fmt(cart.itemsPrice)}
                </span>
              </div>
              <div className="hadacy-placeorder__row">
                <span className="hadacy-placeorder__row-label">
                  Costo de envío
                </span>
                <span className="hadacy-placeorder__row-value">
                  {Number(cart.shippingPrice) === 0
                    ? "Gratis"
                    : `$${fmt(cart.shippingPrice)}`}
                </span>
              </div>
              <div className="hadacy-placeorder__total-row">
                <span className="hadacy-placeorder__total-label">Total</span>
                <div className="hadacy-placeorder__total-amounts">
                  {hasDiscount && (
                    <span className="hadacy-placeorder__total-strike">
                      ${fmt(cart.totalPrice)}
                    </span>
                  )}
                  <span className="hadacy-placeorder__total-amount">
                    ${fmt(hasDiscount ? totalPriceDiscount : cart.totalPrice)}
                  </span>
                  {userInfo && userInfo.isAdmin && (
                    <div>
                      <span className="hadacy-placeorder__admin-pill">
                        Admin discount
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Coupon */}
              <div className="hadacy-placeorder__coupon-block">
                <label className="hadacy-placeorder__coupon-label">
                  Cupón de descuento
                </label>
                <div className="hadacy-placeorder__coupon-row">
                  <Form.Control
                    type="text"
                    value={coupon}
                    placeholder="CÓDIGO"
                    onChange={(e) => setCoupon(e.target.value)}
                    className="hadacy-placeorder__coupon-input"
                  />
                  <button
                    type="button"
                    className="hadacy-placeorder__coupon-apply"
                    onClick={() => checkCoupon(coupon)}
                  >
                    Aplicar
                  </button>
                </div>
                {couponMessage && (
                  <p
                    className={
                      "hadacy-placeorder__coupon-msg " +
                      (couponOk && hasDiscount
                        ? "hadacy-placeorder__coupon-msg--ok"
                        : "hadacy-placeorder__coupon-msg--err")
                    }
                  >
                    {couponMessage}
                    {couponOk && hasDiscount && cart.coupon && (
                      <>
                        <br />
                        <strong>
                          {cart.coupon.Type === "PERCENT"
                            ? `${cart.coupon.Discount}% OFF`
                            : `$${fmt(cart.coupon.appliedDiscountAmount)} OFF`}
                        </strong>
                      </>
                    )}
                  </p>
                )}
              </div>

              {error && (
                <div className="hadacy-placeorder__error">
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{error}</span>
                </div>
              )}

              <button
                type="button"
                className="hadacy-placeorder__cta"
                disabled={cart.cartItems.length === 0}
                onClick={placeOrderHandler}
              >
                Completar orden
                <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PlaceOrderScreen;
